import React from "react";
import { FaShieldAlt, FaGlobeAmericas, FaUsers, FaCubes, FaTwitter, FaLinkedin, FaGithub } from "react-icons/fa";
import { Link } from "react-router-dom";

const About = () => {
  const teamMembers = [
    {
      name: "Alex Thompson",
      role: "Founder & CEO",
      bio: "Blockchain developer with 8+ years of experience. Previously led development at major DeFi projects.",
      image: "/api/placeholder/150/150"
    },
    {
      name: "Sarah Chen",
      role: "CTO",
      bio: "Smart contract expert and former security auditor. Leads our technical development and security protocols.",
      image: "/api/placeholder/150/150"
    },
    {
      name: "Michael Rodriguez",
      role: "Head of Operations",
      bio: "Fintech veteran with experience at major payment processors. Oversees day-to-day operations and compliance.",
      image: "/api/placeholder/150/150"
    },
    {
      name: "Emma Wilson",
      role: "Lead Arbitrator",
      bio: "Former attorney specialized in dispute resolution. Manages our network of independent arbitrators.",
      image: "/api/placeholder/150/150"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white pt-24 pb-16">
      {/* Hero Section */}
      <section className="container mx-auto px-6 mb-20">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-green-400 to-blue-500 text-transparent bg-clip-text">
            About Escrownia
          </h1>
          <p className="text-xl text-gray-300 mb-10">
            We're on a mission to make digital transactions secure, transparent, and accessible for everyone through blockchain technology.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="container mx-auto px-6 mb-20">
        <div className="flex flex-col md:flex-row gap-12 items-center">
          <div className="md:w-1/2">
            <h2 className="text-3xl font-bold mb-6">Our Story</h2>
            <p className="text-gray-300 mb-4">
              Founded in 2023, Escrownia emerged from a simple vision: to create a trustless escrow platform that leverages blockchain technology to make digital transactions secure and accessible for everyone.
            </p>
            <p className="text-gray-300 mb-4">
              Our team experienced firsthand the challenges of conducting high-value transactions online—the lack of trust, the risk of fraud, and the high fees charged by traditional escrow services. We knew there had to be a better way.
            </p>
            <p className="text-gray-300">
              Using blockchain's inherent security and smart contract capabilities, we built Escrownia to eliminate these pain points, creating a platform where funds are only released when predetermined conditions are met, all with complete transparency and minimal fees.
            </p>
          </div>
          
          <div className="md:w-1/2">
            <div className="relative">
              <img src="/api/placeholder/500/400" alt="Escrownia Team" className="rounded-lg w-full" />
              <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-blue-600 opacity-20 rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="bg-gray-800 bg-opacity-50 py-20 mb-20">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-6">Our Core Values</h2>
            <p className="text-gray-300">
              These principles guide everything we do at Escrownia, from product development to customer support.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500 transition-all duration-300 h-full">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <FaShieldAlt className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4">Security First</h3>
              <p className="text-gray-400">
                We never compromise on security. Our platform is built with multiple layers of protection to safeguard your funds and data.
              </p>
            </div>
            
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500 transition-all duration-300 h-full">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <FaCubes className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4">Blockchain Innovation</h3>
              <p className="text-gray-400">
                We leverage the latest blockchain technologies to create trustless systems that eliminate the need for blind faith in transactions.
              </p>
            </div>
            
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500 transition-all duration-300 h-full">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <FaGlobeAmericas className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4">Global Accessibility</h3>
              <p className="text-gray-400">
                We're building for everyone. Our platform works across borders, currencies, and time zones to connect people worldwide.
              </p>
            </div>
            
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500 transition-all duration-300 h-full">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <FaUsers className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4">Community Focused</h3>
              <p className="text-gray-400">
                We listen to our users and build features that address real needs. Your success is our success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="container mx-auto px-6 mb-20">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl font-bold mb-6">Meet Our Team</h2>
          <p className="text-gray-300">
            We're a diverse group of blockchain enthusiasts, developers, and security experts passionate about creating a trustless future.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {teamMembers.map((member, index) => (
            <div key={index} className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-blue-500 transition-all duration-300">
              <div className="mb-6 relative">
                <img 
                  src={member.image} 
                  alt={member.name} 
                  className="w-32 h-32 rounded-full mx-auto object-cover border-2 border-gray-700"
                />
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-green-500 to-blue-600 opacity-0 hover:opacity-20 transition-opacity duration-300"></div>
              </div>
              <h3 className="text-xl font-bold text-center mb-2">{member.name}</h3>
              <p className="text-blue-400 text-center text-sm mb-4">{member.role}</p>
              <p className="text-gray-400 text-center text-sm">{member.bio}</p>
              <div className="flex justify-center mt-4 space-x-3">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <FaTwitter />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <FaLinkedin />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <FaGithub />
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-6">
        <div className="bg-gradient-to-r from-green-600 to-blue-700 rounded-2xl p-12 text-center">
          <h2 className="text-3xl font-bold mb-6">Join the Escrownia Community</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Be part of the revolution in secure digital transactions. Start using Escrownia today.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <Link
              to="/"
              className="px-8 py-4 rounded-lg text-lg font-semibold bg-white text-gray-900 hover:bg-gray-100 transition-all duration-300 shadow-lg"
            >
              Get Started
            </Link>
            <a
              href="#"
              className="px-8 py-4 rounded-lg text-lg font-semibold border-2 border-white hover:bg-white hover:text-gray-900 transition-all duration-300 shadow-lg"
            >
              Join Our Discord
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;